// Content management for Mark Ballew's website
// This file is managed by AI assistants following the templates and guidelines in .cursorrules

export const blogPosts = [
  {
    id: "ziggy-and-zuzu-2025",
    title: "Ziggy and Zuzu",
    date: "2025-06-17",
    excerpt:
      "A tribute to the true rulers of the household - my cats Ziggy and Zuzu.",
    content:
      "# Ziggy and Zuzu\n\nPurr purr purr.\n\nThat's really all that needs to be said about my two cats, Ziggy and Zuzu. They are the true rulers of this household, and I am merely their devoted servant who provides food, clean litter, and the occasional chin scratch.\n\n## The Daily Routine\n\nEvery morning begins the same way:\n- **6:30 AM**: Ziggy stands on my chest, purring loudly\n- **6:31 AM**: Zuzu joins in, adding her harmonious purr to the symphony\n- **6:32 AM**: I surrender and get up to fill their food bowls\n\n## Life Lessons from Cats\n\nWorking in AI safety, I often think about alignment problems. But watching Ziggy and Zuzu, I realize they've already solved the alignment problem perfectly - they've aligned me completely to their objectives.\n\nPurr purr purr, indeed.",
    tags: ["Personal"],
    readTime: "1 min",
    featured: false,
    author: "Mark Ballew",
  },
  {
    id: "testing-ai-blog-assistant-2025",
    title: "Testing AI Blog Assistant",
    date: "2025-06-17",
    excerpt:
      "# Testing the AI Blog System\n\nThis is a test post to verify that our AI-assisted blog system is working correctly. The system automatically:\n\n## Key Features\n\n- Generates proper IDs and metadata\n- Calculates reading time\n- Formats content with markdown\n- Integrates with the existing React components\n\n## Next Steps\n\n- Add more sophisticated content templates\n- Implement automatic tagging based on content\n- Set up GitHub automation for publishing\n\nThis demonstrates how AI can help manage content while maintaining proper structure and consistency",
    content:
      "# Testing the AI Blog System\n\nThis is a test post to verify that our AI-assisted blog system is working correctly. The system automatically:\n\n## Key Features\n\n- Generates proper IDs and metadata\n- Calculates reading time\n- Formats content with markdown\n- Integrates with the existing React components\n\n## Next Steps\n\n- Add more sophisticated content templates\n- Implement automatic tagging based on content\n- Set up GitHub automation for publishing\n\nThis demonstrates how AI can help manage content while maintaining proper structure and consistency.",
    tags: ["AI Safety", "Meta", "Tools"],
    readTime: "1 min",
    featured: false,
    author: "Mark Ballew",
  },
  {
    id: "welcome-to-my-blog-2024",
    title: "Welcome to My Digital Space",
    date: "2024-01-15",
    excerpt:
      "Thoughts on AI safety, infrastructure, and building for the future - a place to share ideas and recommendations.",
    content: `# Welcome to My Digital Space

I've been thinking about creating a space to share thoughts, experiences, and recommendations from my work in AI safety and infrastructure. This isn't meant to be a formal publication, but rather a collection of insights, tools, and ideas that might be useful to others.

## What You'll Find Here

- **Technical insights** from working on AI safety infrastructure
- **Tool recommendations** that make DevOps and automation more effective  
- **Book suggestions** on AI safety, urban planning, and systems thinking
- **Thoughts on transportation** and building better cities
- **Random experiments** with 3D printing and automation

## Why Share?

I believe in building transparent, composable systems that keep humans in control. That applies to more than just infrastructure - it's about sharing knowledge and tools that help everyone build better things.

Looking forward to sharing more soon!`,
    tags: ["Personal", "Meta"],
    readTime: "2 min",
    featured: true,
    author: "Mark Ballew",
  },
];

export const recommendations = {
  books: [
    {
      id: "superintelligence",
      title: "Superintelligence: Paths, Dangers, Strategies",
      author: "Nick Bostrom",
      category: "AI Safety",
      rating: 5,
      notes:
        "Essential reading for understanding AI alignment challenges and the importance of getting AI safety right.",
      link: "https://www.goodreads.com/book/show/20527133-superintelligence",
      featured: true,
    },
    {
      id: "happy-city",
      title: "Happy City: Transforming Our Lives Through Urban Design",
      author: "Charles Montgomery",
      category: "Urban Planning",
      rating: 5,
      notes:
        "Brilliant exploration of how city design affects human happiness and well-being. Essential for anyone interested in transportation and urban policy.",
      link: "https://www.goodreads.com/book/show/13330588-happy-city",
      featured: false,
    },
  ],
  tools: [
    {
      id: "opentofu",
      name: "OpenTofu",
      category: "Infrastructure",
      description:
        "Open-source Terraform alternative for infrastructure as code. Community-driven and fully compatible with Terraform.",
      link: "https://opentofu.org",
      why: "More transparent governance than Terraform, active community development, and maintains compatibility.",
      featured: true,
    },
    {
      id: "github-actions",
      name: "GitHub Actions",
      category: "CI/CD",
      description:
        "Native CI/CD platform integrated with GitHub. Perfect for automation and deployment pipelines.",
      link: "https://github.com/features/actions",
      why: "Seamless integration with GitHub repos, extensive marketplace of actions, and great for security automation.",
      featured: true,
    },
  ],
  resources: [
    {
      id: "metr-evals",
      name: "METR AI Safety Evaluations",
      category: "AI Safety",
      description:
        "Research and methodologies for evaluating AI capabilities and safety.",
      link: "https://metr.org",
      why: "Leading nonprofit organization doing cutting-edge research on AI evaluation and safety testing.",
      featured: true,
    },
  ],
};

export const projects = [
  {
    id: "website-ai-management",
    title: "AI-Managed Website Content",
    description:
      "Using AI assistants to manage blog posts and content through GitHub commits and Amplify deployment.",
    status: "Active",
    technologies: ["React", "GitHub API", "AWS Amplify", "AI"],
    featured: true,
    learnings:
      "Exploring how AI can handle content management while maintaining human oversight and version control.",
  },
];

// Hackathons and demos (add entries as you go)
export const hackathons = [
  {
    id: "zkx402-ethglobal-buenos-aires-2025",
    event: "ETHGlobal Buenos Aires",
    year: 2025,
    project: "zkx402 (Proof of Leak)",
    award: "Finalist",
    description:
      "x402 protocol extension with ZK-based verification for variable payments + verifiable content provenance and identity-based pricing tiers.",
    technologies: ["ZK", "Next.js", "TypeScript", "x402", "Base"],
    links: [
      {
        label: "Showcase",
        url: "https://ethglobal.com/showcase/zkx402-proofofleak-wxtjf",
      },
      {
        label: "Full implementation (repo)",
        url: "https://github.com/vhspace/zkx402",
      },
    ],
  },
  {
    id: "decentralized-roasting-ethrome-2025",
    event: "ETHRome",
    year: 2025,
    project: "Decentralized roasting",
    description:
      "Agents on ElizaOS get paid to roast your photos. ENS-based agent discovery, Base payments, XMTP messaging, and shareable results.",
    technologies: ["AI Agents", "ENS", "XMTP", "Base", "ElizaOS"],
    links: [
      {
        label: "Hackathon page",
        url: "https://taikai.network/en/ethrome/hackathons/2025/projects/cmgwhg5en01351nb9o8vvtp59/idea",
      },
      {
        label: "Demo (sometimes offline)",
        url: "https://ai-roast-generator-ivory.vercel.app",
      },
    ],
  },
  {
    id: "trustdai-ethdenver-2025",
    event: "ETHDenver",
    year: 2025,
    project: "TrustDAI",
    award: "okto: Sixth Place (Consumer DApps) + 2 other prizes",
    description:
      "Own your AI data—store, control, and track access using decentralized storage and smart contracts. Built around user-controlled privacy and portability.",
    technologies: ["Solidity", "React", "TypeScript", "Smart Contracts"],
    links: [
      {
        label: "Devfolio",
        url: "https://devfolio.co/projects/trustdai-da7a",
      },
    ],
  },
];

// Curated / highlighted work (selected contributions/projects)
export const selectedOpenSourceWork = [
  {
    id: "vhspace-zkx402",
    title: "vhspace/zkx402",
    venue: "Open source",
    type: "Project",
    role: "Contributor",
    description:
      "Full implementation of zkx402 (x402 protocol extension with ZK). This is the primary codebase for the project.",
    tags: ["ZK", "Protocols", "TypeScript", "Solidity"],
    link: "https://github.com/vhspace/zkx402",
  },
  {
    id: "vhspace-pp2p",
    title: "vhspace/pp2p",
    venue: "Open source",
    type: "Project",
    role: "Contributor",
    description:
      "Contributed substantial implementation work and ongoing maintenance.",
    tags: ["Open Source"],
    link: "https://github.com/vhspace/pp2p",
  },
  {
    id: "metr-inspect-action",
    title: "METR/inspect-action",
    venue: "Open source",
    type: "Contribution",
    role: "Major contributor",
    description:
      "GitHub Action for running UK AISI's Inspect evaluations on Kubernetes. Helped open-source the repo and contributed improvements via extensive commits and maintenance work.",
    tags: ["GitHub Actions", "CI/CD", "Open Source"],
    link: "https://github.com/METR/inspect-action",
  },
];

// Content templates for AI assistants to follow
export const contentTemplates = {
  blogPost: {
    requiredFields: [
      "id",
      "title",
      "date",
      "excerpt",
      "content",
      "tags",
      "readTime",
      "author",
    ],
    optionalFields: ["featured", "image", "links"],
    idFormat: "kebab-case-title-year",
    excerptLength: "1-2 sentences, under 150 characters",
    contentFormat: "Markdown with proper headers, code blocks if technical",
    tagGuidelines:
      "Use existing tags when possible: [AI Safety, DevOps, Infrastructure, Transportation, Personal, Meta, Tools]",
    styleGuide:
      "Technical but accessible, conversational tone, practical insights",
  },
  recommendation: {
    books: {
      requiredFields: ["id", "title", "author", "category", "rating", "notes"],
      optionalFields: ["link", "featured"],
      categories: [
        "AI Safety",
        "Urban Planning",
        "Technology",
        "Fiction",
        "Systems Thinking",
      ],
    },
    tools: {
      requiredFields: ["id", "name", "category", "description", "link", "why"],
      optionalFields: ["featured"],
      categories: [
        "Infrastructure",
        "CI/CD",
        "Development",
        "Automation",
        "Security",
      ],
    },
    resources: {
      requiredFields: ["id", "name", "category", "description", "link", "why"],
      optionalFields: ["featured"],
      categories: ["AI Safety", "Transportation", "Technology", "Learning"],
    },
  },
  hackathon: {
    requiredFields: ["id", "event", "project"],
    optionalFields: ["year", "award", "description", "technologies", "links"],
    idFormat: "kebab-case-project-year",
  },
};

// Helper functions for content management
export const getLatestPosts = (count = 3) => {
  return blogPosts
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, count);
};

export const getFeaturedContent = () => {
  return {
    posts: blogPosts.filter((post) => post.featured),
    books: recommendations.books.filter((book) => book.featured),
    tools: recommendations.tools.filter((tool) => tool.featured),
    resources: recommendations.resources.filter(
      (resource) => resource.featured
    ),
  };
};

export const getPostById = (id) => {
  return blogPosts.find((post) => post.id === id);
};

export const getPostsByTag = (tag) => {
  return blogPosts.filter((post) => post.tags.includes(tag));
};
